<script setup>
import { ref, watch, onMounted } from 'vue'
import { useCustomChart } from '@/composable/customChart.js'
import { getFontSize, getThisYear } from '@/js/customChart.js'
import { useUserStore } from '@/store/user'
const userStore = useUserStore()

const chartOptions = {
  padding: 10,
  columns: 4,
  rows: 4,
}
const { canvasWrap, initCustomChart } = useCustomChart(drawChart, chartOptions, false)

import { getPipelineManagementList } from '@/api/dashboard'

// props
const props = defineProps({
  gridArea: Object,
  employeeId: String,
  width: Number,
  height: Number,
  fiscalYear:String
})

// data
const fetchedData = ref()

// watch
watch([() => props.employeeId, ()=>props.fiscalYear], async (newValue) => {
  try {
    const [employeeId, fiscalYear] = newValue
    const result = await getPipelineManagementList({
      fs_year: fiscalYear,
      employee_id: employeeId
    })
    fetchedData.value = result.rtn.at(0)
    initCustomChart()
  } catch (err) {
    console.error(err)
  }
})

// lifecycle
onMounted(async () => {
  try {
    const employeeId = userStore.isAdmin ? '' : userStore.user.employeeId
    const result = await getPipelineManagementList({
      fs_year: props.fiscalYear,
      employee_id: props.employeeId || employeeId
    })
    fetchedData.value = result.rtn.at(0)
    initCustomChart()
  } catch (err) {
    console.error(err)
  }
})

function drawChart(canvasInfo, ctx) {
  const mappedData = Object.entries(fetchedData.value)
    .map(([key, value], index) => {
      const units = {
        pipelinefirstper: '%',
        cnt: '',
        pipelinesecondper: 'x',
        pipelinethirdper: 'x'
      }
      const coords = {
        pipelinefirstper: [1, 1],
        cnt: [3, 1],
        pipelinesecondper: [1, 3],
        pipelinethirdper: [3, 3]
      }
      const condition = {
        pipelinefirstper: [80, 100],
        cnt: '',
        pipelinesecondper:  [1.5, 2],
        pipelinethirdper: [1.5, 2.5]
      }
      const obj = {
        key,
        value,
        unit: units[key],
        coords: coords[key],
      }
      if (obj.unit) {
        obj.style = {
          fillStyle: getFontColor(value, ...condition[key])
        }
      }
      return obj
    })

  mappedData.forEach(datum => {
    drawText(canvasInfo, ctx, datum)
  })
}

function drawText(canvasInfo, ctx, {key, value, unit = '', coords: [ x, y ], style = {} }) {
  const coords = [
    canvasInfo.cellWidth * x + canvasInfo.padding,
    canvasInfo.cellHeight * y + canvasInfo.padding,
  ]
  const initStyle = {
    textAlign: 'center',
    textBaseline: 'middle',
    fillStyle: 'black',
  }
  const mergedStyle = {
    ...initStyle,
    ...style
  }
  mergedStyle.font = `${getFontSize(canvasInfo, props.gridArea, 14)}px suit-m`
  if (Object.keys(mergedStyle).length) {
    Object.entries(mergedStyle).forEach(([key, value]) => {
      ctx[key] = value
    }) 
  }
  ctx.fillText(`${value}${unit}`, coords[0], coords[1] - getFontSize(canvasInfo, props.gridArea, 6))

  const descriptions = {
    pipelinefirstper: 'FCST % vs Quota', 
    cnt: '# of Commit Deal',
    pipelinesecondper: 'Commit, Won, Close, Upside Size',
    pipelinethirdper: 'All Pipeline Size'
  }
  ctx.font = `bold ${getFontSize(canvasInfo, props.gridArea, 6)}px suit-m`
  ctx.fillStyle = 'black'
  ctx.fillText(`${descriptions[key]}`, coords[0], coords[1] + getFontSize(canvasInfo, props.gridArea, 6))
}

function getFontColor(value, min, max) {
  const condition = {
    red: value < min,
    orange: min <= value && value < max,
    green: max <= value
  }
  const [ color ] = Object.entries(condition).find(item => item[1])
  return color
}
</script>

<template>
  <article :ref="(element) => canvasWrap = element" :style="{ width: `${width}px`, height: `${height}px` }">
    <canvas id="PipelineManagement"></canvas>
  </article>
</template>

<style scoped>
article {
  display: flex;
  justify-content: center;
  flex: 1 0 auto;
}
#PipelineManagement {
  width: 100%;
}
</style>